<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  gaiaFontSize,
  gaiaTracking,
  gaiaFontWeight,
  gaiaNeutral,
  gaiaMotion,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import { NDynamicTags, NFormItem, type SelectOption } from 'naive-ui';
import { TemplateFieldType, type TemplateField } from '@/api';
import {
  fieldHasInput,
  getCurrencyCode,
} from '@/composables/contractGeneration/useExtractedSchema';
import { useFieldVisibility } from '@/composables/contractGeneration/useConditionalVisibility';
import { useTemplateFieldMetadata } from '@/composables/contractGeneration/useTemplateFieldMetadata';
import {
  resolveFieldLabel,
  resolveFieldProse,
} from '@/utils/contractGeneration/templateFieldLabels';
import GaiaText from '@/components/design/base/GaiaText.vue';
import FieldGuidanceHint from './FieldGuidanceHint.vue';
import FilterHintBadge from './FilterHintBadge.vue';
import ListField from './ListField.vue';
import ObjectField from './ObjectField.vue';
import EntityField from './EntityField.vue';
import { isEntityFieldType } from '@/services/contractGeneration/entityFieldService';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaDateField from '@/components/design/base/inputs/GaiaDateField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';

interface Props {
  fieldKey: string;
  field: TemplateField;
  modelValue: unknown;
  rootFormData: Record<string, unknown>;
  /**
   * Scope chain from innermost enclosing object/item to outermost, not
   * including the root form data. A condition like `"is_international"`
   * resolves against the chain in order, then falls back to `rootFormData`.
   * Having a chain (rather than a single scope) lets a condition inside a
   * nested list item reference a field on the enclosing list's current item.
   */
  localScopes?: unknown[];
  path: string;
  invalidPaths: Set<string>;
  disabled?: boolean;
  /** When true, render as a top-level group (no card wrapping for objects). */
  topLevel?: boolean;
  /**
   * When true, suppress this field's own label/header because the parent
   * (e.g. a section header in FieldEntryPane) already shows it. Only the
   * input(s) + filter hints + feedback render.
   */
  hideContainerHeader?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:modelValue': [value: unknown];
}>();

// --- Visibility / labels / container detection ---------------------------

const rawVisible = useFieldVisibility(
  () => props.field,
  () => props.rootFormData,
  () => props.localScopes ?? []
);

// Optional object fields with a SELF-REFERENTIAL condition are the backend's
// way of saying "this block appears when the user chooses to add it" — e.g.
// `client.representative` with `condition: "client.representative"`. For
// those we skip the condition check so the Add/Remove toggle is always
// reachable. Optional objects with a NON-self-referential condition (e.g.
// `spouse` with `condition: "is_married"`) are real visibility gates and
// should fall through to the normal evaluator.
const visible = computed<boolean>(() => {
  const t = props.field.type;
  const objectLike =
    t === TemplateFieldType.OBJECT ||
    t === TemplateFieldType.LEGAL_ENTITY ||
    t === TemplateFieldType.NATURAL_PERSON;
  if (objectLike && !props.field.required) {
    const ast = props.field.condition_ast;
    if (!ast) return true;
    // A self-referential condition (e.g. `client.representative` gating a
    // field whose own path is `client.representative`) just means "the user
    // toggles to add this section" — always show the Add/Remove toggle so
    // it's reachable.
    if (ast.kind === 'path') {
      const lastSegment = ast.path.split('.').pop();
      if (ast.path === props.path || lastSegment === props.fieldKey) return true;
    }
  }
  return rawVisible.value;
});

// Whoever renders the label renders the author's prose with it; with the
// header hidden the parent shows both, so none of these appear here then.
const meta = useTemplateFieldMetadata(() => props.path);
const label = computed(() => resolveFieldLabel(meta.value, props.fieldKey));
const description = computed(() => resolveFieldProse(meta.value?.description));
const guidance = computed(() => resolveFieldProse(meta.value?.guidance));
const invalid = computed(() => props.invalidPaths.has(props.path));
const currencyCode = computed(() =>
  props.field.type === TemplateFieldType.NUMBER ? getCurrencyCode(props.field) : null
);
const isEntity = computed(() => isEntityFieldType(props.field.type));
const isEntityList = computed(
  () =>
    props.field.type === TemplateFieldType.LIST && isEntityFieldType(props.field.item_type ?? null)
);
const isContainer = computed(
  () =>
    props.field.type === TemplateFieldType.OBJECT ||
    props.field.type === TemplateFieldType.LIST ||
    isEntity.value
);

/**
 * A template field with a `default_value` is effectively optional — the
 * user can leave it blank and the template's `default(...)` filter will
 * substitute at render time. The "*" required mark and the validator both
 * follow this.
 */
const effectivelyRequired = computed(
  () => Boolean(props.field.required) && !props.field.default_value
);

/** Use the default_value as the input's placeholder so the user sees it
 *  as an inline hint of "this is what you'll get if you skip me". */
const defaultPlaceholder = computed<string | undefined>(
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to undefined
  () => props.field.default_value || undefined
);

/** Helper text shown below the input when a default exists — communicates
 *  the "leave blank → default applies" contract. */
const defaultHint = computed<string>(() =>
  props.field.default_value ? `Leave blank to use the default (${props.field.default_value})` : ''
);

function handleUpdate(value: unknown): void {
  emit('update:modelValue', value);
}

// --- Primitive: string (commit-on-blur buffer) --------------------------
//
// A local buffer keeps keystrokes snappy and — critically — prevents the
// live preview from refetching on every character typed. Parent sees the
// new value only on blur, Enter, or clear. Where Enter goes next is
// FieldEntryPane's keyboard flow; here it only commits.
const stringBuffer = ref<string>(typeof props.modelValue === 'string' ? props.modelValue : '');

// --- Primitive: number (same buffer pattern) -----------------------------
const numberBuffer = ref<number | null>(
  typeof props.modelValue === 'number' ? props.modelValue : null
);

// External updates (e.g. draft restore, sibling field update) must flow back
// into the buffers. Route by field type so only the relevant buffer is touched.
watch(
  () => props.modelValue,
  incoming => {
    if (props.field.type === TemplateFieldType.STRING) {
      const next = typeof incoming === 'string' ? incoming : '';
      if (next !== stringBuffer.value) stringBuffer.value = next;
    } else if (props.field.type === TemplateFieldType.NUMBER) {
      const next = typeof incoming === 'number' ? incoming : null;
      if (next !== numberBuffer.value) numberBuffer.value = next;
    }
  }
);

function commitString(): void {
  const parent = typeof props.modelValue === 'string' ? props.modelValue : '';
  if (stringBuffer.value !== parent) emit('update:modelValue', stringBuffer.value);
}
function clearString(): void {
  stringBuffer.value = '';
  commitString();
}
const isMultiline = computed(() => stringBuffer.value.length > 80);

function commitNumber(): void {
  const parent = typeof props.modelValue === 'number' ? props.modelValue : null;
  if (numberBuffer.value !== parent) emit('update:modelValue', numberBuffer.value);
}
function clearNumber(): void {
  numberBuffer.value = null;
  commitNumber();
}

// --- Primitive: date -----------------------------------------------------
//
// GaiaDateField speaks the same ISO "YYYY-MM-DD" the form state holds, so
// there is nothing to translate — it handles typed dates and the calendar
// panel itself.
const dateValue = computed<string | null>(() =>
  typeof props.modelValue === 'string' && props.modelValue.length > 0 ? props.modelValue : null
);

// --- Primitive: boolean --------------------------------------------------
//
// A Yes / No dropdown rather than a switch: a switch always reads as an
// answer, so a required question could never be seen as unanswered, and
// "No" and "not yet filled in" looked the same. The dropdown keeps the
// third state — empty — until the person picks. Filterable, so "y" or "n"
// and Enter answers it without the mouse.
const BOOLEAN_OPTIONS: SelectOption[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

const booleanValue = computed<string | null>({
  get: () => {
    if (props.modelValue === true) return 'true';
    if (props.modelValue === false) return 'false';
    return null;
  },
  set: value => {
    emit('update:modelValue', value === null ? null : value === 'true');
  },
});

// --- Primitive: simple string-list (for `list` without item_fields) ------
const simpleListValue = computed<string[]>({
  get: () => {
    if (!Array.isArray(props.modelValue)) return [];
    return props.modelValue.filter((item): item is string => typeof item === 'string');
  },
  set: value => {
    emit('update:modelValue', value);
  },
});

// --- Primitive: selection (dropdown of pre-defined string options) -------
const selectionValue = computed<string | null>({
  get: () => (typeof props.modelValue === 'string' ? props.modelValue : null),
  set: value => {
    emit('update:modelValue', value);
  },
});

const selectionOptions = computed<SelectOption[]>(() =>
  (props.field.selection_options ?? []).map(option => ({ label: option, value: option }))
);

/**
 * A required field that now holds an answer gets a small check beside its
 * label — the moment of "done" at the field itself, not only in the section
 * count. Optional fields stay quiet: a check on every answer would be noise.
 */
const showFilledCheck = computed<boolean>(
  () => effectivelyRequired.value && !invalid.value && fieldHasInput(props.field, props.modelValue)
);

// --- chrome (tokens for the scoped styles) ---
const gSuccess = gaiaStatus.success;
const easeOut = gaiaMotion.easeOut;
const dStandard = gaiaMotion.standard;
const dQuick = gaiaMotion.quick;
const fsBody = gaiaFontSize.body;
const fwSemibold = String(gaiaFontWeight.semibold);
const fwBold = String(gaiaFontWeight.bold);
const trackCaps = gaiaTracking.labelSoft;
const fsMeta = gaiaFontSize.meta;
const gAlert = gaiaStatus.alert;
const gInk = gaiaNeutral.ink;
const gSubtle = gaiaNeutral.textSubtle;
</script>

<template>
  <div v-if="visible" class="schema-field" :class="{ 'schema-field--container': isContainer }">
    <template v-if="isContainer && !hideContainerHeader">
      <div class="container-header">
        <div class="container-label">
          <span class="label-text">{{ label }}</span>
          <span v-if="field.required" class="required-mark">*</span>
          <field-guidance-hint v-if="guidance" :text="guidance" :style="{ marginLeft: '4px' }" />
        </div>
        <filter-hint-badge :filters="field.filters" />
      </div>
      <gaia-text v-if="description" size="meta" tone="subtle" :style="{ lineHeight: 1.45 }">
        {{ description }}
      </gaia-text>
    </template>

    <!-- Primitive fields: everything non-container lives under the same
         NFormItem shell. The switch-arms are trivial enough to inline here
         instead of each having its own component file. -->
    <NFormItem
      v-if="!isContainer"
      :label="hideContainerHeader ? undefined : label"
      :show-label="!hideContainerHeader"
      :show-require-mark="false"
      :validation-status="invalid ? 'error' : undefined"
      :feedback="invalid ? 'Required' : defaultHint || undefined"
      :show-feedback="invalid || Boolean(defaultHint)"
      label-placement="top"
    >
      <!-- The label row spans the field: name, the asterisk (drawn here, not
           by naive, so it stays next to the name), the "?" guidance and the
           filled check on the left; the formatting wand on the right. The
           inputs below keep their full width whatever a field carries. -->
      <template v-if="!hideContainerHeader" #label>
        <n-flex
          align="center"
          justify="space-between"
          :size="8"
          :wrap="false"
          class="field-label-row"
        >
          <n-flex inline align="center" :size="6" :wrap="false">
            {{ label }}
            <span v-if="effectivelyRequired" class="required-mark">*</span>
            <field-guidance-hint v-if="guidance" :text="guidance" />
            <transition name="field-check">
              <span v-if="showFilledCheck" class="field-check" aria-label="Filled in">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2.5 6.5 5 9l4.5-6" />
                </svg>
              </span>
            </transition>
          </n-flex>
          <!-- The currency prefix on a number input already says how it is
               formatted, so the wand is left off there. -->
          <filter-hint-badge
            v-if="field.type !== 'number' || !currencyCode"
            :filters="field.filters"
          />
        </n-flex>
      </template>
      <!-- The author's description reads before the control; the form item's
           own feedback line stays for "Required" and the default hint. -->
      <div class="field-input-column">
        <gaia-text
          v-if="description && !hideContainerHeader"
          size="meta"
          tone="subtle"
          :style="{ lineHeight: 1.45 }"
        >
          {{ description }}
        </gaia-text>
        <div class="field-input">
          <gaia-input
            v-if="field.type === 'string'"
            v-model:value="stringBuffer"
            :type="isMultiline ? 'textarea' : 'text'"
            :autosize="isMultiline ? { minRows: 2, maxRows: 6 } : undefined"
            :status="invalid ? 'error' : undefined"
            :disabled="disabled"
            :placeholder="defaultPlaceholder"
            clearable
            @blur="commitString"
            @keydown.enter="commitString"
            @clear="clearString"
          />
          <gaia-input-number
            v-else-if="field.type === 'number'"
            v-model:value="numberBuffer"
            :status="invalid ? 'error' : undefined"
            :disabled="disabled"
            :show-button="false"
            :placeholder="defaultPlaceholder"
            clearable
            style="width: 100%"
            @blur="commitNumber"
            @keydown.enter="commitNumber"
            @clear="clearNumber"
          >
            <template v-if="currencyCode" #prefix>
              <span class="currency-prefix">{{ currencyCode }}</span>
            </template>
          </gaia-input-number>
          <gaia-date-field
            v-else-if="field.type === 'date'"
            :value="dateValue"
            :status="invalid ? 'error' : undefined"
            :disabled="disabled"
            :placeholder="defaultPlaceholder"
            @update:value="handleUpdate"
          />
          <gaia-select
            v-else-if="field.type === 'boolean'"
            v-model:value="booleanValue"
            :options="BOOLEAN_OPTIONS"
            :status="invalid ? 'error' : undefined"
            :disabled="disabled"
            placeholder="Yes or no"
            filterable
            clearable
          />
          <gaia-select
            v-else-if="field.type === 'selection'"
            v-model:value="selectionValue"
            :options="selectionOptions"
            :status="invalid ? 'error' : undefined"
            :disabled="disabled"
            :placeholder="defaultPlaceholder ?? 'Pick one'"
            filterable
            clearable
          />
        </div>
      </div>
    </NFormItem>

    <!-- Containers: objects recurse through ObjectField (which handles the
         optional Add/Remove toggle + nested SchemaField children). Lists
         with item_fields recurse through ListField; lists of primitives
         render inline as a tag input since there's nothing to recurse into.
         Entity fields (legal_entity / natural_person) render through
         EntityField, which adds search-existing + save-as-new on top of the
         same SchemaField recursion for the manual fallback. -->
    <EntityField
      v-else-if="isEntity"
      :field-key="fieldKey"
      :field="field"
      :model-value="modelValue"
      :root-form-data="rootFormData"
      :local-scopes="localScopes ?? []"
      :path="path"
      :invalid-paths="invalidPaths"
      :disabled="disabled"
      :hide-container-header="hideContainerHeader"
      @update:model-value="handleUpdate"
    />

    <ObjectField
      v-else-if="field.type === 'object'"
      :field="field"
      :model-value="modelValue"
      :root-form-data="rootFormData"
      :path="path"
      :invalid-paths="invalidPaths"
      :disabled="disabled"
      :parent-scopes="localScopes ?? []"
      :flat="topLevel"
      @update:model-value="handleUpdate"
    />

    <template v-else-if="field.type === 'list'">
      <ListField
        v-if="field.item_fields"
        :field="field"
        :model-value="modelValue"
        :root-form-data="rootFormData"
        :path="path"
        :invalid-paths="invalidPaths"
        :disabled="disabled"
        :parent-scopes="localScopes ?? []"
        :entity-item-type="isEntityList ? (field.item_type ?? null) : null"
        @update:model-value="handleUpdate"
      />
      <NDynamicTags v-else v-model:value="simpleListValue" :disabled="disabled" />
    </template>
  </div>
</template>

<style scoped>
.schema-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  animation: schema-field-in 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
  animation-delay: var(--schema-field-delay, 0ms);
}

@keyframes schema-field-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/* The "filled in" check beside a required field's label: pops in a touch
   past its size and settles, leaves with a plain fade. */
.field-check {
  display: inline-flex;
  align-items: center;
  color: v-bind(gSuccess);
}

.field-check-enter-active {
  animation: field-check-in v-bind(dStandard) v-bind(easeOut);
}

.field-check-leave-active {
  transition: opacity v-bind(dQuick) ease;
}

.field-check-leave-to {
  opacity: 0;
}

@keyframes field-check-in {
  from {
    opacity: 0;
    transform: scale(0.6);
  }
  60% {
    transform: scale(1.15);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .schema-field,
  .field-check-enter-active {
    animation: none;
  }
}

/* The control takes the row; the formatting wand sits at its right end,
   lined up with the first line of the control (34px input, 24px wand). */
.field-input {
  min-width: 0;
}

/* The label row has to reach the field's right edge for the wand to sit
   there: naive's label text span is inline, so both it and the row stretch. */
.schema-field :deep(.n-form-item-label) {
  width: 100%;
}

.schema-field :deep(.n-form-item-label__text) {
  flex: 1 1 auto;
  min-width: 0;
}

.field-label-row {
  width: 100%;
}

.schema-field--container {
  gap: 8px;
}

.container-header {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}

.container-label {
  display: flex;
  gap: 2px;
  align-items: baseline;
}

.label-text {
  font-weight: v-bind(fwSemibold);
  font-size: v-bind(fsBody);
  color: v-bind(gInk);
}

.required-mark {
  color: v-bind(gAlert);
  font-weight: v-bind(fwBold);
}

.field-input-column {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  width: 100%;
}

.currency-prefix {
  font-size: v-bind(fsMeta);
  font-weight: v-bind(fwSemibold);
  color: v-bind(gSubtle);
  letter-spacing: v-bind(trackCaps);
}
</style>
